import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { CalendarDays, Check, X, Clock } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

function formatDate(d) {
  if (!d) return "-";
  return new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
}

export default function TargetDateRequestsPage() {
  const { user } = useAuth();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("pending");

  const fetchRequests = useCallback(async () => {
    setLoading(true);
    try {
      const params = filter !== "all" ? { status: filter } : {};
      const res = await axios.get(`${API}/api/tasks/target-date-requests`, { params });
      setRequests(res.data);
    } catch {
      setRequests([]);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { fetchRequests(); }, [fetchRequests]);

  const handleAction = async (requestId, action) => {
    try {
      await axios.put(`${API}/api/tasks/target-date-requests/${requestId}/${action}`);
      toast.success(action === "approve" ? "Target date change approved" : "Request rejected");
      fetchRequests();
    } catch (err) {
      toast.error(err.response?.data?.detail || `Failed to ${action}`);
    }
  };

  const isSuperAdmin = user?.role === "super_admin";

  return (
    <div className="p-6 space-y-6" data-testid="target-date-requests-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl tracking-tight flex items-center gap-2">
            <CalendarDays className="h-6 w-6" /> Target Date Change Requests
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Review and approve/reject task target date change requests
          </p>
        </div>
        <div className="w-40">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="h-9" data-testid="status-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
              <SelectItem value="all">All</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="border rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/50">
              <TableHead>Task</TableHead>
              <TableHead>Order</TableHead>
              <TableHead>Department</TableHead>
              <TableHead>Old Date</TableHead>
              <TableHead>New Date</TableHead>
              <TableHead>Reason</TableHead>
              <TableHead>Requested By</TableHead>
              <TableHead>Status</TableHead>
              {isSuperAdmin && <TableHead className="text-center">Actions</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={9} className="text-center py-8 text-muted-foreground">Loading...</TableCell></TableRow>
            ) : requests.length === 0 ? (
              <TableRow><TableCell colSpan={9} className="text-center py-8 text-muted-foreground">No requests found</TableCell></TableRow>
            ) : (
              requests.map((req) => (
                <TableRow key={req.request_id} data-testid={`request-row-${req.request_id}`}>
                  <TableCell className="text-sm font-medium max-w-[180px] truncate">{req.task_name}</TableCell>
                  <TableCell className="text-sm">{req.order_number}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className="text-xs capitalize">{req.department}</Badge>
                  </TableCell>
                  <TableCell className="text-sm">{formatDate(req.old_target_date)}</TableCell>
                  <TableCell className="text-sm font-medium">{formatDate(req.new_target_date)}</TableCell>
                  <TableCell className="text-sm max-w-[200px] truncate">{req.reason}</TableCell>
                  <TableCell className="text-sm">{req.requested_by_name}</TableCell>
                  <TableCell>
                    <Badge
                      variant={req.status === "pending" ? "secondary" : req.status === "approved" ? "default" : "destructive"}
                      className="text-xs"
                    >
                      {req.status === "pending" && <Clock className="h-3 w-3 mr-1" />}
                      {req.status === "approved" && <Check className="h-3 w-3 mr-1" />}
                      {req.status === "rejected" && <X className="h-3 w-3 mr-1" />}
                      {req.status}
                    </Badge>
                  </TableCell>
                  {isSuperAdmin && (
                    <TableCell>
                      {req.status === "pending" ? (
                        <div className="flex items-center justify-center gap-1">
                          <Button
                            size="sm"
                            variant="default"
                            className="h-7 text-xs"
                            onClick={() => handleAction(req.request_id, "approve")}
                            data-testid={`approve-${req.request_id}`}
                          >
                            <Check className="h-3 w-3 mr-1" /> Approve
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            className="h-7 text-xs"
                            onClick={() => handleAction(req.request_id, "reject")}
                            data-testid={`reject-${req.request_id}`}
                          >
                            <X className="h-3 w-3 mr-1" /> Reject
                          </Button>
                        </div>
                      ) : (
                        <span className="text-xs text-muted-foreground text-center block">-</span>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
